import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { NOME_LARGURA, NOME_X, TAGLINE } from './Marca.js';
import { CABECA, CAIXA_COMPACTA, CRINA, EQUI, NOME_NA_COMPACTA, SIMBOLO_NA_COMPACTA, VIEW } from './marca-desenho.js';

/**
 * Tela de entrada: a logo se desenhando. O contorno de cada forma do simbolo
 * (a linha da cabeca, a crina, orelhas, olho e narina) e tracado como a mao,
 * depois as formas ganham a cor, o nome "Equiview" se revela da esquerda para
 * a direita e a frase aparece. No fim, a logo compacta com a frase, igual a de
 * `Marca.tsx`, e o botao "Entrar". So CSS e SVG; com "reduzir movimento",
 * aparece tudo pronto.
 */

/**
 * Ordem do traco: a grande linha da cabeca, a crina, e o resto do maior para o
 * menor (indices de CABECA, na ordem do SVG da marca).
 */
const ORDEM_DA_CABECA = [2, 1, 0, 3, 4, 5, 6];
export const FORMAS_DO_SIMBOLO: Array<{ d: string; crina: boolean }> = [
  { d: CABECA[ORDEM_DA_CABECA[0]!]!, crina: false },
  { d: CRINA, crina: true },
  ...ORDEM_DA_CABECA.slice(1).map((i) => ({ d: CABECA[i]!, crina: false })),
];

const [, , LARGURA, ALTURA] = CAIXA_COMPACTA.split(' ').map(Number) as [number, number, number, number];
const ALTURA_COM_FRASE = ALTURA + 26;

const atraso = (s: number) => ({ '--atraso': `${s}s` }) as CSSProperties;

export function TelaDeEntrada({ onEntrar }: { onEntrar: () => void }) {
  const [saindo, setSaindo] = useState(false);
  const botao = useRef<HTMLButtonElement>(null);

  // O foco ja no botao: Enter entra, sem precisar do mouse.
  useEffect(() => botao.current?.focus({ preventScroll: true }), []);

  const entrar = () => {
    if (saindo) return;
    setSaindo(true);
    setTimeout(onEntrar, 200);
  };

  return (
    <main
      aria-label="Entrada do Equiview"
      className={`fixed inset-0 z-50 flex flex-col items-center justify-center gap-12 overflow-hidden bg-abismo bg-[radial-gradient(ellipse_at_50%_45%,color-mix(in_oklab,var(--color-aco)_22%,transparent),transparent_60%)] px-6 pt-[max(1.5rem,env(safe-area-inset-top))] pb-[max(1.5rem,env(safe-area-inset-bottom))] text-gelo transition-opacity duration-200 ease-out ${
        saindo ? 'opacity-0' : 'opacity-100'
      }`}
    >
      <h1 className="w-[min(84vw,560px)]">
        <svg viewBox={`0 0 ${LARGURA} ${ALTURA_COM_FRASE}`} role="img" aria-label="Equiview" className="h-auto w-full overflow-visible">
          <defs>
            {/* O nome se revela da esquerda para a direita, como uma caneta passando. */}
            <clipPath id="entrada-nome">
              <rect x={NOME_X - 8} y={0} width={LARGURA - NOME_X + 8} height={ALTURA} className="entrada-revelar" style={atraso(1.6)} />
            </clipPath>
          </defs>

          <g transform={SIMBOLO_NA_COMPACTA}>
            {FORMAS_DO_SIMBOLO.map((f, i) => (
              <path
                key={i}
                d={f.d}
                pathLength={1}
                strokeWidth={6}
                strokeLinejoin="round"
                strokeLinecap="round"
                className={`entrada-desenho ${f.crina ? 'fill-nevoa stroke-nevoa' : 'fill-gelo stroke-gelo'}`}
                // Traco: um apos o outro. Tinta: todas juntas, quando o desenho fecha.
                style={{ '--atraso': `${i * 0.12}s`, '--tinta': '1.2s' } as CSSProperties}
              />
            ))}
          </g>

          <g clipPath="url(#entrada-nome)" data-nome>
            <g transform={NOME_NA_COMPACTA}>
              <g className="fill-gelo">
                {EQUI.map((d) => (
                  <path key={d.slice(0, 24)} d={d} />
                ))}
              </g>
              <g className="fill-nevoa">
                {VIEW.map((d) => (
                  <path key={d.slice(0, 24)} d={d} />
                ))}
              </g>
            </g>
          </g>

          <text
            x={NOME_X}
            y={ALTURA + 16}
            textLength={NOME_LARGURA}
            lengthAdjust="spacing"
            className="entrada-surgir fill-gelo/75 font-sans"
            style={{ ...atraso(2.0), fontSize: 12.5, fontWeight: 600 }}
          >
            {TAGLINE}
          </text>
        </svg>
      </h1>

      <button
        ref={botao}
        type="button"
        onClick={entrar}
        className="entrada-surgir alvo-toque h-12 rounded-(--radius-controle) bg-gelo px-12 text-base font-semibold text-abismo transition-transform duration-100 ease-out hover:bg-white focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-nevoa active:scale-[0.97]"
        style={atraso(2.3)}
      >
        Entrar
      </button>
    </main>
  );
}
